import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { StyleSheet, css } from '@coursera/coursera-ui';

import AuthenticationV3CtaTracking from 'bundles/authentication/shared/AuthenticationV3CtaTracking';
import Separator from 'bundles/authentication/shared/Separator';
import SignupForm from 'bundles/authentication/shared/SignupForm';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { MODES } from 'bundles/authentication/shared/components/authentication-modal/AuthenticationContent';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import SocialSSO from 'bundles/authentication/shared/components/authentication-modal/SocialSSO';
import type SSOError from 'bundles/authentication/shared/components/authentication-modal/SocialSSO/SSOError';
import type { ArkoseBotManagerContext, SuccessResponse } from 'bundles/authentication/shared/types/sharedTypes';
import { shouldShowLegalConsolidationAuthFlow, shouldShowTOSDisclaimer } from 'bundles/authentication/utils';
import {
  shouldShowSocialSSOButtonsAboveEmailSignup,
  shouldShowSocialSSOButtonsBelowEmailSignup,
} from 'bundles/authentication/utils/experimentUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/authentication';

type Props = {
  className?: string;
  navigate: (path: MODES, options?: $TSFixMe) => void;
  onSignup: (redirectURL: string, options?: { addShowTouAcceptParam?: boolean }) => void;
  onSSOSuccess: (isRegistration: boolean) => void;
  onSSOError: (error: SSOError) => void;
  title?: string;
  tagline?: string;
  arkoseBotManagerContext?: ArkoseBotManagerContext;
};

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);

const styles = StyleSheet.create({
  container: {
    display: 'grid',
    gridGap: '32px',
    gridTemplateColumns: '1fr',
    margin: '0 auto',
    maxWidth: '396px',
    padding: '32px 16px',
    '@media (min-width: 600px)': {
      padding: '12px 16px',
    },
  },
  tagLine: {
    margin: '8px auto 0px',
    textAlign: 'center',
    width: '220px',
  },
  title: {
    color: '#1F1F1F',
    fontFamily: 'OpenSans',
    fontSize: '1.5rem',
    lineHeight: '2rem',
    margin: '0',
    textAlign: 'center',
    '@media (min-width: 600px)': {
      fontSize: '1.75rem',
      lineHeight: '2.25rem',
    },
  },
  navigation: {
    margin: '16px 0 0',
    textAlign: 'center',
  },
  link: {
    textDecoration: 'underline',
  },
  organization: {
    margin: '0',
    textAlign: 'center',
  },
  separator: {
    margin: '24px 0',
  },
  terms: {
    color: '#616161',
    fontSize: '12px',
    lineHeight: '20px',
    margin: '16px 0 0',
    padding: '0 32px',
    textAlign: 'center',
    ':first-of-type': {
      margin: '0',
    },
    // workaround for nested selectors
    ':nth-of-type(n) a': {
      color: 'inherit',
      textDecoration: 'underline',
    },
  },
});

const Signup = ({
  navigate,
  onSignup,
  onSSOSuccess,
  onSSOError,
  className,
  title,
  tagline,
  arkoseBotManagerContext,
}: Props) => {
  const navigateToLogin = () => {
    navigate(MODES.LOGIN);
  };

  const navigateToOrganizationSSO = () => {
    navigate(MODES.ORGANIZATION, { referrer: MODES.SIGNUP });
  };

  const handleSuccess = (response: SuccessResponse) => {
    Instrumentation.register();

    onSignup(response.redirectTo, { addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow() });
  };

  const handleSSOSuccess = (isRegistration: boolean) => {
    onSSOSuccess(isRegistration);
  };

  const isInvisible = GrowthAcquisitionExperiments.get('useEnterpriseInvisibleReCaptcha');
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');
  const isEnterprise = isInvisible || isCheckbox;

  return (
    <section {...css(className, styles.container)} data-catchpoint="authentication-box-content">
      <header {...css(styles.header, styles.section)}>
        <h1 {...css(styles.title)}>{title ?? _t('Sign up')}</h1>
        <p {...css(styles.tagLine)}>
          {tagline ?? (
            <FormattedMessage message={_t('Learn on your own time from top \n universities and businesses.')} />
          )}
        </p>
      </header>
      <div {...css(styles.section)}>
        {shouldShowSocialSSOButtonsAboveEmailSignup() && (
          <>
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />
            <Separator {...css(styles.separator)} text={_t('or')} />
          </>
        )}
        <SignupForm
          navigateToLogin={navigateToLogin}
          onSuccess={handleSuccess}
          arkoseBotManagerContext={arkoseBotManagerContext}
        />
        {shouldShowSocialSSOButtonsBelowEmailSignup() && (
          <>
            <Separator {...css(styles.separator)} text={_t('or')} />
            <SocialSSO onError={onSSOError} onSuccess={handleSSOSuccess} />
          </>
        )}
        <p {...css(styles.navigation)}>
          <FormattedMessage
            message={_t('Already on Coursera? {loginLink}')}
            loginLink={
              <AuthenticationV3CtaTracking>
                {(trackV3) => {
                  const handleOnClick = () => {
                    trackV3({ name: 'login' });
                    navigateToLogin();
                  };
                  return (
                    <TrackedLinkButton onClick={handleOnClick} trackingName="login" {...css(styles.link)}>
                      {_t('Log in')}
                    </TrackedLinkButton>
                  );
                }}
              </AuthenticationV3CtaTracking>
            }
          />
        </p>
        <Separator {...css(styles.separator)} />
        <p {...css(styles.organization)}>
          <AuthenticationV3CtaTracking>
            {(trackV3) => {
              const handleOnClick = () => {
                trackV3({ name: 'signup_with_other_sso' });
                navigateToOrganizationSSO();
              };
              return (
                <TrackedLinkButton
                  onClick={handleOnClick}
                  trackingName="sign_up_with_organization_btn_click"
                  {...css(styles.link)}
                >
                  {_t('Sign up with your organization')}
                </TrackedLinkButton>
              );
            }}
          </AuthenticationV3CtaTracking>
        </p>
      </div>
      <div {...css(styles.section)}>
        <p {...css(styles.terms)}>
          {shouldShowTOSDisclaimer() && (
            <FormattedMessage
              message={_t("I accept Coursera's {tosLink} and {privacyLink}. ")}
              privacyLink={
                <a href="/about/privacy" target="_blank">
                  {_t('Privacy Notice')}
                </a>
              }
              tosLink={
                <a href="/about/terms" target="_blank">
                  {_t('Terms of Use')}
                </a>
              }
            />
          )}
          <FormattedMessage
            message={_t('Having trouble logging in? {helpCenterLink}')}
            helpCenterLink={
              <a href="https://learner.coursera.help/hc/articles/209818553" target="_blank" rel="noopener noreferrer">
                {_t('Learner help center')}
              </a>
            }
          />
        </p>
        <p {...css(styles.terms)}>
          <FormattedMessage
            message={
              isEnterprise
                ? _t(
                    'This site is protected by reCAPTCHA Enterprise and the Google {privacyPolicyLink} and {tosLink} apply.'
                  )
                : _t('This site is protected by reCAPTCHA and the Google {privacyPolicyLink} and {tosLink} apply.')
            }
            privacyPolicyLink={
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                {_t('Privacy Policy')}
              </a>
            }
            tosLink={
              <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                {_t('Terms of Service')}
              </a>
            }
          />
        </p>
      </div>
    </section>
  );
};

export default Signup;
